<template>
  <div class="fsign-page">
    <!-- ==================== 概览头 ==================== -->
    <div class="fsp-hero">
      <div class="fsp-hero-l">
        <div class="fsp-hero-title">
          <el-button icon="ArrowLeft" text @click="go({ path: '/formList' })">返回</el-button>
          <h2>签署留痕</h2>
          <span class="fsp-hero-badge">电子签署</span>
        </div>
        <p class="fsp-hero-sub">
          电子签署按「五要素」留痕：签名图 / 协议正文快照 / 内容哈希 / 时间戳 / 签署人身份。
          列表里的哈希标识用于快速判断「这条签署是否具备可复现的凭据」。
        </p>
      </div>
      <div class="fsp-hero-r">
        <el-button icon="Refresh" @click="refresh">刷 新</el-button>
      </div>
    </div>

    <!-- ==================== 筛选条 ==================== -->
    <div class="fsp-bar">
      <el-select v-model="query.formId" clearable filterable placeholder="全部表单" class="fsp-sel-form">
        <el-option v-for="f in formOptions" :key="f.id" :label="f.title" :value="f.id"></el-option>
      </el-select>
      <el-input
          v-model="query.keyword"
          clearable
          placeholder="签署人姓名"
          prefix-icon="Search"
          class="fsp-keyword"
          @keyup.enter="search"></el-input>
      <el-button type="primary" icon="Search" @click="search">查询</el-button>
      <el-button icon="RefreshLeft" @click="reset">重置</el-button>
      <span class="fsp-count">共 {{ pageTotal }} 条</span>
    </div>

    <!-- ==================== 表格 ==================== -->
    <div class="fsp-card">
      <el-table
          v-loading="listLoading"
          :data="tableData"
          :cell-style="{padding: '6px 0'}"
          row-key="id"
          class="fsp-table">
        <el-table-column min-width="170" show-overflow-tooltip label="表单">
          <template #default="scope">{{ scope.row.formTitle || '—' }}</template>
        </el-table-column>
        <el-table-column width="110" label="签署人">
          <template #default="scope">{{ scope.row.signerName || '—' }}</template>
        </el-table-column>
        <el-table-column min-width="180" show-overflow-tooltip label="协议标题">
          <template #default="scope">{{ scope.row.agreementTitle || '—' }}</template>
        </el-table-column>
        <el-table-column width="110" align="center" label="记录号">
          <template #default="scope">{{ scope.row.recordId || '—' }}</template>
        </el-table-column>
        <el-table-column width="120" align="center" label="留痕">
          <template #default="scope">
            <el-tag :type="traceTagType(scope.row)" size="small">{{ traceText(scope.row) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column width="160" label="签署时间">
          <template #default="scope">{{ scope.row.signedAt || '—' }}</template>
        </el-table-column>
        <el-table-column width="90" align="center" fixed="right" label="操作">
          <template #default="scope">
            <el-button size="small" text type="primary" @click="openDetail(scope.row)">查看</el-button>
          </template>
        </el-table-column>

        <template #empty>
          <div v-if="listFailed" class="fsp-fail">
            <el-icon><WarningFilled /></el-icon>
            <p>{{ listError || '签署留痕加载失败' }}</p>
            <el-button type="primary" icon="RefreshRight" @click="selectList">重试</el-button>
          </div>
          <div v-else class="fsp-empty">
            <el-icon><Checked /></el-icon>
            <p>{{ emptyText }}</p>
            <el-button v-if="hasFilter" text type="primary" @click="reset">重置筛选</el-button>
          </div>
        </template>
      </el-table>
    </div>

    <div class="pagination">
      <el-pagination
          background
          layout="total, sizes, prev, pager, next, jumper"
          :page-sizes="[10, 20, 50, 100]"
          :current-page="query.pageIndex"
          :page-size="query.pageSize"
          :total="pageTotal"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"></el-pagination>
    </div>

    <!-- ==================== 详情抽屉 ==================== -->
    <el-drawer v-model="detailVisible" title="签署留痕详情" size="560px">
      <div class="fsp-detail">
        <div class="fsp-detail-title">{{ detail.formTitle || '—' }}</div>
        <el-divider content-position="left">五要素</el-divider>
        <div class="fsp-meta">
          <div class="fsp-meta-item">
            <span class="fsp-meta-k">签署人</span>
            <span>{{ detail.signerName || '—' }}</span>
          </div>
          <div class="fsp-meta-item">
            <span class="fsp-meta-k">时间戳</span>
            <span>{{ detail.signedAt || '—' }}</span>
          </div>
          <div class="fsp-meta-item">
            <span class="fsp-meta-k">协议标题</span>
            <span>{{ detail.agreementTitle || '—' }}</span>
          </div>
          <div class="fsp-meta-item">
            <span class="fsp-meta-k">记录号</span>
            <span>{{ detail.recordId || '—' }}</span>
          </div>
          <div class="fsp-meta-item fsp-meta-item--wide">
            <span class="fsp-meta-k">快照哈希</span>
            <span class="fsp-mono">{{ detail.snapshotHash || '—' }}</span>
          </div>
          <div class="fsp-meta-item fsp-meta-item--wide">
            <span class="fsp-meta-k">内容哈希</span>
            <span class="fsp-mono">{{ detail.dataHash || '—' }}</span>
          </div>
        </div>

        <el-divider content-position="left">签名图</el-divider>
        <div class="fsp-sign-box">
          <img v-if="detail.signImage" :src="detail.signImage" alt="签名" class="fsp-sign-img"/>
          <span v-else class="fsp-none">无签名图（该记录为「只需勾选」档）</span>
        </div>

        <el-divider content-position="left">协议快照</el-divider>
        <div class="fsp-snap">
          <el-button v-if="detail.snapshotUrl" type="primary" link @click="openSnapshot">打开协议快照</el-button>
          <span v-else class="fsp-none">无快照（未开启「保存协议快照」）</span>
        </div>
        <div class="fsp-tip">
          内容哈希 = SHA-256(协议快照 + 表单值摘要)。只要快照与哈希都在，任意历史签署都能逐字节复现当时协议内容。
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import { formPage, formSignPage } from '@/api/form';

export default {
  name: 'FormSign',
  data() {
    return {
      listLoading: false,
      listFailed: false,
      listError: '',
      tableData: [],
      pageTotal: 0,
      formOptions: [],
      query: {
        pageIndex: 1,
        pageSize: 10,
        formId: null,
        keyword: '',
      },
      detailVisible: false,
      detail: {},
    };
  },
  computed: {
    hasFilter() {
      return this.query.formId != null || !!this.query.keyword;
    },
    emptyText() {
      if (this.hasFilter) return '没有符合筛选条件的签署留痕';
      return '还没有签署留痕 —— 电子签署是表单级可选项，未开启签署的表单不会产生记录';
    },
  },
  methods: {
    /** 五要素齐全（两个哈希都在）才算「可复现」 */
    traceOk(row) {
      return !!(row && row.snapshotHash && row.dataHash);
    },
    traceText(row) {
      return this.traceOk(row) ? '已留痕' : '凭据不全';
    },
    traceTagType(row) {
      return this.traceOk(row) ? 'success' : 'warning';
    },
    loadFormOptions() {
      formPage({ pageIndex: 1, pageSize: 200, keyword: '' }).then(val => {
        if (val && val.code === 200) {
          const records = (val.data && val.data.records) || [];
          this.formOptions = records.map(r => ({ id: r.id, title: r.title || ('#' + r.id) }));
        }
      }).catch(() => { /* 下拉拉取失败不影响主列表 */ });
    },
    selectList() {
      this.listLoading = true;
      this.listFailed = false;
      this.listError = '';
      formSignPage(this.query).then(val => {
        this.listLoading = false;
        if (val && val.code === 200) {
          this.tableData = (val.data && val.data.records) || [];
          this.pageTotal = (val.data && val.data.total) || 0;
        } else {
          this.tableData = [];
          this.pageTotal = 0;
          this.listFailed = true;
          this.listError = (val && val.message) || '签署留痕加载失败';
        }
      }).catch((err) => {
        this.listLoading = false;
        this.tableData = [];
        this.pageTotal = 0;
        this.listFailed = true;
        this.listError = (err && err.message) || '签署留痕加载失败，请检查网络';
      });
    },
    refresh() {
      this.selectList();
    },
    search() {
      this.query.pageIndex = 1;
      this.refresh();
    },
    reset() {
      this.query.formId = null;
      this.query.keyword = '';
      this.query.pageIndex = 1;
      this.refresh();
    },
    handleSizeChange(size) {
      this.query.pageSize = size;
      this.query.pageIndex = 1;
      this.selectList();
    },
    handlePageChange(index) {
      this.query.pageIndex = index;
      this.selectList();
    },
    openDetail(row) {
      this.detail = row || {};
      this.detailVisible = true;
    },
    openSnapshot() {
      const url = this.detail && this.detail.snapshotUrl;
      if (!url) return;
      this.$confirm('将在新窗口打开协议快照（存于 MinIO 的静态化展示版），是否继续？', '打开快照', {
        type: 'info',
        confirmButtonText: '打开',
        cancelButtonText: '取消',
      }).then(() => {
        window.open(url, '_blank');
      }).catch(() => {});
    },
    go(target) {
      const p = this.$router.push(target);
      if (p && typeof p.catch === 'function') {
        p.catch(err => {
          const msg = (err && err.message) || '未知错误';
          this.$message.error(`打开页面失败：${msg}（若是改版后首次进入，请按 Ctrl+F5 强制刷新）`);
        });
      }
    },
  },
  created() {
    const q = this.$route.query.formId;
    this.query.formId = (q != null && q !== '' && !Number.isNaN(Number(q))) ? Number(q) : null;
    this.loadFormOptions();
    this.selectList();
  },
};
</script>

<style scoped>

/* @form-tokens:start */
/* ======================= 表单模块设计令牌（v3.11 视觉统一）=======================
 * ★ 本块在多个表单页各存一份（CSS 变量按作用域隔离，无法跨 scoped 共享）。
 *   改配色的唯一入口 = D:/tmp/shot/form_tokens.py 的 TOKEN_BLOCK，改完重跑该脚本
 *   一键同步；**不要手改单页**，否则多份会漂移。
 * ★ 为什么不放全局 :root：--el-color-primary 一旦全局生效会波及后台所有模块
 *   （订单 / 教练 / 财务…）。本模块只对「表单」相关页负责，故挂在页面根容器上，
 *   作用域精确、零外溢。
 * ========================================================================== */
.fsign-page {
  --el-color-primary: #8dc63f;
  --el-color-primary-light-3: #afd779;
  --el-color-primary-light-5: #c6e39f;
  --el-color-primary-light-7: #ddeec5;
  --el-color-primary-light-8: #e8f4d9;
  --el-color-primary-light-9: #f4f9ec;
  --el-color-primary-dark-2: #719e32;

  /* —— 文字五档（严格对齐 Element 灰阶） —— */
  --yh-t1: #303133; /* 主文字 */
  --yh-t2: #606266; /* 常规 */
  --yh-t3: #909399; /* 次要 */
  --yh-t4: #a8abb2; /* 占位 */
  --yh-t5: #c0c4cc; /* 禁用 */

  /* —— 主色四档 —— */
  --yh-theme: #8dc63f;
  --yh-theme-d: #7cad37;
  --yh-theme-dd: #5f9427;
  --yh-theme-l: #eef7e2;
  --yh-theme-ll: #f6fbef;

  /* —— 面与线 —— */
  --yh-bg: #f5f7fa;
  --yh-fill: #fafbfc;
  --yh-line: #ebeef5;
  --yh-line-d: #dcdfe6;
  --yh-divide: #f2f3f5;

  /* —— 圆角五档 —— */
  --yh-r-xs: 4px;
  --yh-r-sm: 6px;
  --yh-r: 8px;
  --yh-r-lg: 12px;
  --yh-r-pill: 999px;
}
/* @form-tokens:end */

.fsign-page {
  padding: 16px 20px 30px;
  min-height: 100%;
  background-color: #f5f7fa;
  box-sizing: border-box;
}

/* ==================== 概览头 ==================== */
.fsp-hero {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
  background: #fff;
  border-radius: var(--yh-r-lg);
  padding: 16px 20px;
  margin-bottom: 14px;
  box-shadow: 0 1px 4px rgba(0, 0, 21, 0.06);
}

.fsp-hero-title {
  display: flex;
  align-items: center;
  gap: 8px;
}

.fsp-hero-title h2 {
  margin: 0;
  font-size: 19px;
  font-weight: 600;
  color: var(--yh-t1);
}

.fsp-hero-badge {
  padding: 1px 8px;
  border-radius: var(--yh-r-pill);
  background: var(--yh-theme-l);
  color: var(--yh-theme-dd);
  font-size: 12px;
}

.fsp-hero-sub {
  margin: 6px 0 0;
  max-width: 780px;
  font-size: 12px;
  line-height: 1.8;
  color: var(--yh-t3);
}

.fsp-hero-r {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  flex: 0 0 auto;
}

/* ==================== 筛选条 ==================== */
.fsp-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  background: #fff;
  border-radius: var(--yh-r-lg);
  padding: 12px 16px;
  margin-bottom: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 21, 0.06);
}

.fsp-sel-form {
  width: 200px;
}

.fsp-keyword {
  width: 170px;
}

.fsp-count {
  margin-left: auto;
  font-size: 12px;
  color: var(--yh-t3);
}

/* ==================== 表格卡 ==================== */
.fsp-card {
  background: #fff;
  border-radius: var(--yh-r-lg);
  padding: 6px 12px 14px;
  box-shadow: 0 1px 4px rgba(0, 0, 21, 0.06);
}

.fsp-table :deep(.el-table__inner-wrapper::before) {
  display: none;
}

.fsp-table :deep(.el-table__header th) {
  background: var(--yh-fill);
  color: var(--yh-t2);
  font-weight: 600;
}

.fsp-table :deep(.el-table__row:hover > td) {
  background: var(--yh-theme-ll) !important;
}

.pagination {
  margin-top: 16px;
  text-align: right;
}

/* 空态 / 失败态 */
.fsp-empty,
.fsp-fail {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 26px 0 12px;
}

.fsp-empty .el-icon,
.fsp-fail .el-icon {
  font-size: 32px;
  color: #dcdfe6;
}

.fsp-fail .el-icon {
  color: #e6a23c;
}

.fsp-empty p,
.fsp-fail p {
  margin: 0;
  font-size: 13px;
  color: var(--yh-t4);
}

/* ==================== 详情抽屉 ==================== */
.fsp-detail {
  padding: 0 4px;
}

.fsp-detail-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--yh-t1);
  margin-bottom: 10px;
}

.fsp-meta {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 16px;
}

.fsp-meta-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--yh-t1);
}

.fsp-meta-item--wide {
  grid-column: 1 / -1;
}

.fsp-meta-k {
  flex: 0 0 68px;
  color: var(--yh-t3);
}

.fsp-mono {
  font-family: Consolas, Monaco, monospace;
  font-size: 12px;
  color: var(--yh-t2);
  word-break: break-all;
}

.fsp-sign-box {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 90px;
  border: 1px dashed var(--yh-line-d);
  border-radius: var(--yh-r);
  background: var(--yh-fill);
  padding: 8px;
}

.fsp-sign-img {
  max-width: 100%;
  max-height: 160px;
}

.fsp-snap {
  padding: 4px 0;
}

.fsp-none {
  font-size: 13px;
  color: var(--yh-t4);
}

.fsp-tip {
  margin-top: 10px;
  font-size: 12px;
  line-height: 1.7;
  color: var(--yh-t3);
}
</style>
